import React, { useEffect, useMemo, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { useReserveContext } from '../../../contexts/useReserveContext';
import {
  fetchAvailableTimesByStaff,
  type AvailableSlotByStaff as AvailableSlot,
} from '../../../utils/reservation/by-staff/fetchAvailableTimesByStaff';
import { isPastTimeSlot } from '../../../utils/reservation/filterPastTimeSlots';

import ReserveStepHeader from '../ReserveStepHeader';
import ReserveStepFooter from '../ReserveStepFooter';
import { useI18n } from '../../../i18n';

const SelectTimeByStaff: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useI18n();
  const [searchParams] = useSearchParams();
  const isEditing = searchParams.get('edit') === '1';
  const {
    storeId,
    selectedDate,
    selectedStaffId,
    selectedServices,
    selectedStartTime,
    setSelectedStartTime,
  } = useReserveContext();

  const [slots, setSlots] = useState<AvailableSlot[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!storeId) {
      toast.info(t('date.selectStoreFirst'));
      navigate('/reserve/by-staff/store');
      return;
    }
    if (!selectedStaffId) {
      toast.info(t('date.selectStaffFirst'));
      navigate('/reserve/by-staff/staff');
      return;
    }
    if (!selectedDate) {
      toast.info(t('date.chooseTitle'));
      navigate('/reserve/by-staff/date');
      return;
    }
    if (selectedServices.length === 0) {
      toast.info(t('date.selectServiceFirst'));
      navigate('/reserve/by-staff/service');
      return;
    }
  }, [storeId, selectedStaffId, selectedDate, selectedServices.length, navigate, t]);

  const totalMinutes = useMemo(
    () => selectedServices.reduce((sum, s) => sum + (Number(s.duration_minutes) || 0), 0),
    [selectedServices],
  );
  const serviceIds = useMemo(
    () =>
      selectedServices
        .map((service) => Number(service.serviceId))
        .filter((serviceId) => Number.isFinite(serviceId) && serviceId > 0),
    [selectedServices],
  );

  useEffect(() => {
    const load = async () => {
      if (!storeId || !selectedDate || !selectedStaffId || totalMinutes <= 0) return;
      setLoading(true);
      try {
        const data = await fetchAvailableTimesByStaff({
          storeId,
          staffId: selectedStaffId,
          date: selectedDate,
          durationMinutes: totalMinutes,
          serviceIds,
        });
        setSlots(data);
      } catch (err) {
        console.error(err);
        toast.error(t('time.loadFailed'));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [storeId, selectedDate, selectedStaffId, totalMinutes, serviceIds, t]);

  useEffect(() => {
    if (!selectedStartTime || !selectedDate) return;

    const selectedSlot = slots.find((slot) => slot.time === selectedStartTime);
    if (!selectedSlot || !selectedSlot.available || isPastTimeSlot(selectedDate, selectedStartTime)) {
      setSelectedStartTime(null);
    }
  }, [slots, selectedDate, selectedStartTime, setSelectedStartTime]);

  const handleSelectTime = (time: string, available: boolean) => {
    if (!available || (selectedDate && isPastTimeSlot(selectedDate, time))) return;
    setSelectedStartTime(time);
  };

  const handleNext = () => {
    if (!selectedStartTime) {
      toast.warning(t('time.selectWarning'));
      return;
    }
    navigate('/reserve/by-staff/confirm-reservation');
  };

  return (
    <div className="flex flex-col min-h-[70vh] space-y-4">
      <ReserveStepHeader
        title={t('time.chooseTitle')}
        onBack={() =>
          navigate(isEditing ? '/reserve/by-staff/confirm-reservation' : '/reserve/by-staff/service')
        }
        step={5}
        totalSteps={5}
      />

      {loading ? (
        <p className="text-center text-slate-400 text-sm mt-2">{t('time.loading')}</p>
      ) : slots.length === 0 ? (
        isEditing ? (
          <div className="rounded-[28px] border border-rose-200 bg-rose-50 p-4 shadow-sm">
            <div className="flex items-start gap-3">
              <div className="rounded-full bg-rose-100 p-2 text-rose-600">
                <AlertTriangle className="h-4 w-4" />
              </div>
              <div className="min-w-0">
                <div className="text-sm font-semibold text-rose-700">{t('time.cannotChange')}</div>
                <div className="mt-1 text-sm leading-6 text-rose-600">
                  {t('time.noAlternativeStaff')}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <p className="text-center text-slate-400 text-sm mt-2">
            {t('time.noAvailable')}
          </p>
        )
      ) : (
        <div className="mt-2 rounded-[28px] border border-[#d7efee] bg-[linear-gradient(180deg,rgba(244,253,252,0.98),rgba(255,255,255,0.96))] p-4 shadow-[0_14px_30px_rgba(10,186,181,0.08)]">
          <div className="mb-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#5a9d99]">
            {t('time.availableSlots')}
          </div>
          <div className="grid grid-cols-3 gap-3">
            {slots.map((slot) => {
              const isSelected = selectedStartTime === slot.time;
              const isPast = selectedDate ? isPastTimeSlot(selectedDate, slot.time) : false;
              const isDisabled = !slot.available || isPast;

              return (
                <button
                  key={slot.time}
                  type="button"
                  onClick={() => handleSelectTime(slot.time, slot.available)}
                  disabled={isDisabled}
                  className={[
                    'min-h-[52px] rounded-2xl border text-sm font-semibold transition',
                    isDisabled
                      ? 'bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed'
                      : isSelected
                      ? 'border-[#0ABAB5] bg-[#0ABAB5] text-white shadow-[0_12px_22px_rgba(10,186,181,0.24)]'
                      : 'border-[#d5ecea] bg-white text-slate-700 hover:border-[#9fdeda] hover:bg-[#f4fcfb]',
                  ].join(' ')}
                >
                  {slot.time}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <ReserveStepFooter
        summary={
          selectedStartTime
            ? t('time.selectedSummaryWithMinutes', { time: selectedStartTime, minutes: totalMinutes })
            : t('time.selectSummaryWithMinutes', { minutes: totalMinutes })
        }
        primaryLabel={t('footer.next')}
        onPrimary={handleNext}
        primaryDisabled={!selectedStartTime}
      />
    </div>
  );
};

export default SelectTimeByStaff;
